import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType, BorderStyle, ImageRun } from 'docx';
import type { Invoice, Quotation } from '@/types/invoice';
import { formatCurrency } from './utils';
import { getTemplate, type TemplateId } from '@/components/invoice/InvoiceTemplates';

const cleanHex = (color: string) => color.replace('#', '').toUpperCase();
const FONT_FAMILY = 'Segoe UI';

function getTemplateBackgroundColor(templateId: string): string {
  const bgColors: Record<string, string> = {
    none: '#ffffff',
    clean: '#faf5ff',
    ocean: '#f0f9ff',
    midnight: '#0f172a',
    forest: '#f0fdf4',
    sunset: '#fff7ed',
    rose: '#fff1f2',
    slate: '#f8fafc',
    golden: '#fefdf0',
    aurora: '#faf5ff',
    carbon: '#111827',
  };
  return bgColors[templateId] || '#ffffff';
}


/**
 * Export invoice as PDF
 */
export async function exportToPDF(elementId: string, filename: string): Promise<void> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Invoice element not found');
  }

  try {
    // Capture the element as canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(filename);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw new Error('Failed to export PDF');
  }
}

/**
 * Export invoice as Word document
 */
export async function exportToWord(invoice: Invoice, filename: string): Promise<void> {
  try {
    const businessInfo = (invoice as any).from || (invoice as any)._businessInfo || {};
    const clientInfo = invoice.billTo || (invoice as any).to || {};
    const items = invoice.lineItems || (invoice as any).items || [];
    const taxData = (invoice as any).tax || {};
    const discountData = (invoice as any).discount || {};

    const templateId = ((invoice as any).templateId as TemplateId) || 'clean';
    const tpl = getTemplate(templateId);
    const bgColor = getTemplateBackgroundColor(templateId);

    const children: (Paragraph | Table)[] = [];

    const createSpacer = (before = 100, after = 100) =>
      new Paragraph({
        text: '',
        spacing: { before, after },
      });

    // 1. Header (Side-by-Side)
    const addressLines = (businessInfo.address || '').split('\n');
    const addressParagraphs = addressLines.map((line: string) =>
      new Paragraph({
        children: [
          new TextRun({
            text: line,
            size: 18,
            color: cleanHex(tpl.labelColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 30 },
      })
    );

    const leftCellChildren: any[] = [];

    if (businessInfo.logoUrl) {
      try {
        const response = await fetch(businessInfo.logoUrl);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        // Load image to compute aspect ratio
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = businessInfo.logoUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        const aspectRatio = img.width && img.height ? img.width / img.height : 1.5;
        const targetHeight = 48;
        const targetWidth = Math.min(200, targetHeight * aspectRatio);

        leftCellChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: arrayBuffer,
                transformation: {
                  width: targetWidth,
                  height: targetHeight,
                },
              }),
            ],
            spacing: { after: 100 },
          })
        );
      } catch (err) {
        console.error('Error embedding logo in Word document:', err);
      }
    }

    leftCellChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: businessInfo.name || 'Business Name',
            bold: true,
            size: 28,
            color: cleanHex(tpl.bodyColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
      ...addressParagraphs
    );

    if (businessInfo.email) {
      leftCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: businessInfo.email,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    if (businessInfo.phone) {
      leftCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: businessInfo.phone,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    const rightCellChildren = [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({
            text: 'INVOICE',
            bold: true,
            size: 56,
            color: cleanHex(tpl.headingColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 200 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Invoice #: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({ text: invoice.invoiceNumber, bold: true, color: cleanHex(tpl.bodyColor), size: 20, font: FONT_FAMILY }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Issue Date: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({
            text: invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
            color: cleanHex(tpl.bodyColor),
            size: 20,
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Due Date: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({
            text: invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
            color: cleanHex(tpl.bodyColor),
            size: 20,
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 60 },
      }),
    ];

    const headerTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              children: leftCellChildren,
            }),
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              children: rightCellChildren,
            }),
          ],
        }),
      ],
    });

    children.push(headerTable);
    children.push(createSpacer(200, 200));

    // 2. Bill To Card
    const billToCellChildren = [
      new Paragraph({
        children: [
          new TextRun({
            text: 'Bill To:',
            bold: true,
            size: 20,
            color: cleanHex(tpl.headingColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: clientInfo.name || clientInfo.companyName || 'Client Name',
            bold: true,
            size: 24,
            color: cleanHex(tpl.bodyColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
    ];

    if (clientInfo.address) {
      const clientAddressLines = clientInfo.address.split('\n');
      clientAddressLines.forEach((line: string) => {
        billToCellChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: line,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 30 },
          })
        );
      });
    }

    if (clientInfo.email) {
      billToCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: clientInfo.email,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    if (clientInfo.phone) {
      billToCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: clientInfo.phone,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    const billToTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        left: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        right: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              shading: { fill: cleanHex(tpl.tableHeaderBg) },
              margins: { top: 200, bottom: 200, left: 240, right: 240 },
              children: billToCellChildren,
            }),
          ],
        }),
      ],
    });

    children.push(billToTable);
    children.push(createSpacer(300, 300));

    // 3. Line Items Table (4 Columns)
    const tableRows: TableRow[] = [];

    tableRows.push(
      new TableRow({
        tableHeader: true,
        children: [
          new TableCell({
            width: { size: 50, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Description',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: 'Qty',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Rate',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 20, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Amount',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    items.forEach((item: any) => {
      const hasTax = item.lineTax?.type && item.lineTax.type !== 'none' && (item.lineTax.value ?? 0) > 0;
      const taxStr = hasTax
        ? item.lineTax.type === 'percentage' || item.lineTax.type === 'vat' || item.lineTax.type === 'sst'
          ? `${item.lineTax.value}%`
          : formatCurrency(item.lineTax.value, invoice.currency)
        : '';

      const hasDiscount = item.lineDiscount?.type && item.lineDiscount.type !== 'none' && (item.lineDiscount.value ?? 0) > 0;
      const discountStr = hasDiscount
        ? item.lineDiscount.type === 'percentage'
          ? `${item.lineDiscount.value}%`
          : formatCurrency(item.lineDiscount.value, invoice.currency)
        : '';

      const itemDescChildren = [
        new Paragraph({
          children: [
            new TextRun({
              text: item.description,
              color: cleanHex(tpl.bodyColor),
              font: FONT_FAMILY,
              size: 20,
            }),
          ],
        }),
      ];

      if (hasTax) {
        itemDescChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${item.lineTax.label || 'Tax'}: ${taxStr}`,
                size: 16,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 40 },
          })
        );
      }

      if (hasDiscount) {
        itemDescChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Discount: -${discountStr}`,
                size: 16,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 40 },
          })
        );
      }

      tableRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: itemDescChildren,
            }),
            new TableCell({
              width: { size: 15, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.quantity}`,
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 15, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(item.unitPrice || item.rate || 0, invoice.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 20, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(item.subtotal || item.amount || 0, invoice.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    });

    const itemsTable = new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        insideVertical: { style: BorderStyle.NONE },
      },
    });

    children.push(itemsTable);
    children.push(createSpacer(300, 300));

    // 4. Totals Card
    const totalsRows: TableRow[] = [];

    totalsRows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 60, type: WidthType.PERCENTAGE },
            children: [],
          }),
          new TableCell({
            width: { size: 22, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 100, bottom: 100, left: 160, right: 100 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Subtotal:',
                    color: cleanHex(tpl.labelColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 18, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 100, bottom: 100, left: 100, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: formatCurrency(invoice.subtotal || (invoice as any).subtotal || 0, invoice.currency),
                    color: cleanHex(tpl.bodyColor),
                    font: FONT_FAMILY,
                    size: 20,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    const subtotal = invoice.subtotal || (invoice as any).subtotal || 0;
    const headerTaxes = invoice.settings?.headerTax?.taxes || (taxData && taxData.type !== 'none' ? [taxData] : []);
    headerTaxes.forEach((t: any) => {
      if (!t.type || t.type === 'none') return;
      
      const taxLabel = `${t.label || 'Tax'} (${t.type === 'percentage' || t.type === 'vat' || t.type === 'sst'
        ? `${t.value}%`
        : formatCurrency(t.value, invoice.currency)}):`;
        
      let taxAmount = 0;
      if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
        taxAmount = (subtotal * (t.value || 0)) / 100;
      } else if (t.type === 'fixed') {
        taxAmount = t.value || 0;
      }

      totalsRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 60, type: WidthType.PERCENTAGE },
              children: [],
            }),
            new TableCell({
              width: { size: 22, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 160, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: taxLabel,
                      color: cleanHex(tpl.labelColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 100, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(taxAmount, invoice.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    });

    if (discountData.type && discountData.type !== 'none') {
      const discountLabel = `Discount (${discountData.type === 'percentage'
        ? `${discountData.value}%`
        : formatCurrency(discountData.value, invoice.currency)}):`;
      const discountAmount = (invoice as any).discountAmount || invoice.totalDiscount || 0;

      totalsRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 60, type: WidthType.PERCENTAGE },
              children: [],
            }),
            new TableCell({
              width: { size: 22, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 160, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: discountLabel,
                      color: cleanHex(tpl.labelColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 100, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `-${formatCurrency(discountAmount, invoice.currency)}`,
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    }

    const totalVal = (invoice as any).total || invoice.totalAmount || 0;
    totalsRows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 60, type: WidthType.PERCENTAGE },
            children: [],
          }),
          new TableCell({
            width: { size: 22, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 140, bottom: 140, left: 160, right: 100 },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 8, color: cleanHex(tpl.headingColor) },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Total:',
                    color: cleanHex(tpl.headingColor),
                    font: FONT_FAMILY,
                    size: 24,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 18, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 140, bottom: 140, left: 100, right: 160 },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 8, color: cleanHex(tpl.headingColor) },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: formatCurrency(totalVal, invoice.currency),
                    color: cleanHex(tpl.headingColor),
                    font: FONT_FAMILY,
                    size: 24,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    const totalsTable = new Table({
      rows: totalsRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
    });

    children.push(totalsTable);
    children.push(createSpacer(300, 300));

    // 5. Notes & Terms Separator and Container
    if (invoice.notes || invoice.terms) {
      const notesAndTermsChildren: Paragraph[] = [];

      if (invoice.notes) {
        notesAndTermsChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'Notes:',
                bold: true,
                size: 20,
                color: cleanHex(tpl.headingColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 100, after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: invoice.notes,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 200 },
          })
        );
      }

      if (invoice.terms) {
        notesAndTermsChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'Terms & Conditions:',
                bold: true,
                size: 20,
                color: cleanHex(tpl.headingColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 100, after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: invoice.terms,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 200 },
          })
        );
      }

      const notesAndTermsTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
          insideHorizontal: { style: BorderStyle.NONE },
          insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                margins: { top: 200, bottom: 0, left: 0, right: 0 },
                children: notesAndTermsChildren,
              }),
            ],
          }),
        ],
      });

      children.push(notesAndTermsTable);
    }

    const doc = new Document({
      background: {
        color: cleanHex(bgColor),
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                bottom: 1440,
                left: 1440,
                right: 1440,
              },
            },
          },
          children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting Word:', error);
    throw new Error('Failed to export Word document');
  }
}

export async function exportQuotationToWord(quotation: Quotation, filename: string): Promise<void> {
  try {
    const businessInfo = (quotation as any).from || (quotation as any)._businessInfo || {};
    const clientInfo = quotation.billTo || (quotation as any).to || {};
    const items = quotation.lineItems || (quotation as any).items || [];
    const taxData = (quotation as any).tax || {};
    const discountData = (quotation as any).discount || {};

    const templateId = ((quotation as any).templateId as TemplateId) || 'clean';
    const tpl = getTemplate(templateId);
    const bgColor = getTemplateBackgroundColor(templateId);

    const children: (Paragraph | Table)[] = [];

    const createSpacer = (before = 100, after = 100) =>
      new Paragraph({
        text: '',
        spacing: { before, after },
      });

    // 1. Header (Side-by-Side)
    const addressLines = (businessInfo.address || '').split('\n');
    const addressParagraphs = addressLines.map((line: string) =>
      new Paragraph({
        children: [
          new TextRun({
            text: line,
            size: 18,
            color: cleanHex(tpl.labelColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 30 },
      })
    );

    const leftCellChildren: any[] = [];

    if (businessInfo.logoUrl) {
      try {
        const response = await fetch(businessInfo.logoUrl);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        // Load image to compute aspect ratio
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = businessInfo.logoUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        const aspectRatio = img.width && img.height ? img.width / img.height : 1.5;
        const targetHeight = 48;
        const targetWidth = Math.min(200, targetHeight * aspectRatio);

        leftCellChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: arrayBuffer,
                transformation: {
                  width: targetWidth,
                  height: targetHeight,
                },
              }),
            ],
            spacing: { after: 100 },
          })
        );
      } catch (err) {
        console.error('Error embedding logo in Word document:', err);
      }
    }

    leftCellChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: businessInfo.name || 'Business Name',
            bold: true,
            size: 28,
            color: cleanHex(tpl.bodyColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
      ...addressParagraphs
    );

    if (businessInfo.email) {
      leftCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: businessInfo.email,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    if (businessInfo.phone) {
      leftCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: businessInfo.phone,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    const isEstimate = quotation.quotationNumber?.startsWith('EST');
    const rightCellChildren = [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({
            text: isEstimate ? 'ESTIMATE' : 'QUOTATION',
            bold: true,
            size: 56,
            color: cleanHex(tpl.headingColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 200 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: isEstimate ? 'Estimate #: ' : 'Quotation #: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({ text: quotation.quotationNumber, bold: true, color: cleanHex(tpl.bodyColor), size: 20, font: FONT_FAMILY }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Issue Date: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({
            text: quotation.issueDate ? new Date(quotation.issueDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
            color: cleanHex(tpl.bodyColor),
            size: 20,
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Valid Until: ', color: cleanHex(tpl.labelColor), size: 20, font: FONT_FAMILY }),
          new TextRun({
            text: quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
            color: cleanHex(tpl.bodyColor),
            size: 20,
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 60 },
      }),
    ];

    const headerTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              children: leftCellChildren,
            }),
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              children: rightCellChildren,
            }),
          ],
        }),
      ],
    });

    children.push(headerTable);
    children.push(createSpacer(200, 200));

    // 2. Bill To Card
    const billToCellChildren = [
      new Paragraph({
        children: [
          new TextRun({
            text: 'Quoted To:',
            bold: true,
            size: 20,
            color: cleanHex(tpl.headingColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: clientInfo.name || clientInfo.companyName || 'Client Name',
            bold: true,
            size: 24,
            color: cleanHex(tpl.bodyColor),
            font: FONT_FAMILY,
          }),
        ],
        spacing: { after: 100 },
      }),
    ];

    if (clientInfo.address) {
      const clientAddressLines = clientInfo.address.split('\n');
      clientAddressLines.forEach((line: string) => {
        billToCellChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: line,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 30 },
          })
        );
      });
    }

    if (clientInfo.email) {
      billToCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: clientInfo.email,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    if (clientInfo.phone) {
      billToCellChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: clientInfo.phone,
              size: 18,
              color: cleanHex(tpl.labelColor),
              font: FONT_FAMILY,
            }),
          ],
          spacing: { after: 30 },
        })
      );
    }

    const billToTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        left: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        right: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              shading: { fill: cleanHex(tpl.tableHeaderBg) },
              margins: { top: 200, bottom: 200, left: 240, right: 240 },
              children: billToCellChildren,
            }),
          ],
        }),
      ],
    });

    children.push(billToTable);
    children.push(createSpacer(300, 300));

    // 3. Line Items Table (4 Columns)
    const tableRows: TableRow[] = [];

    tableRows.push(
      new TableRow({
        tableHeader: true,
        children: [
          new TableCell({
            width: { size: 50, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Description',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: 'Qty',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Rate',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 20, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.tableHeaderBg) },
            margins: { top: 160, bottom: 160, left: 160, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Amount',
                    bold: true,
                    color: cleanHex(tpl.tableHeaderColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    items.forEach((item: any) => {
      const hasTax = item.lineTax?.type && item.lineTax.type !== 'none' && (item.lineTax.value ?? 0) > 0;
      const taxStr = hasTax
        ? item.lineTax.type === 'percentage' || item.lineTax.type === 'vat' || item.lineTax.type === 'sst'
          ? `${item.lineTax.value}%`
          : formatCurrency(item.lineTax.value, quotation.currency)
        : '';

      const hasDiscount = item.lineDiscount?.type && item.lineDiscount.type !== 'none' && (item.lineDiscount.value ?? 0) > 0;
      const discountStr = hasDiscount
        ? item.lineDiscount.type === 'percentage'
          ? `${item.lineDiscount.value}%`
          : formatCurrency(item.lineDiscount.value, quotation.currency)
        : '';

      const itemDescChildren = [
        new Paragraph({
          children: [
            new TextRun({
              text: item.description,
              color: cleanHex(tpl.bodyColor),
              font: FONT_FAMILY,
              size: 20,
            }),
          ],
        }),
      ];

      if (hasTax) {
        itemDescChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${item.lineTax.label || 'Tax'}: ${taxStr}`,
                size: 16,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 40 },
          })
        );
      }

      if (hasDiscount) {
        itemDescChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Discount: -${discountStr}`,
                size: 16,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { before: 40 },
          })
        );
      }

      tableRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: itemDescChildren,
            }),
            new TableCell({
              width: { size: 15, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.quantity}`,
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 15, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(item.unitPrice || item.rate || 0, quotation.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 20, type: WidthType.PERCENTAGE },
              margins: { top: 120, bottom: 120, left: 160, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(item.subtotal || item.amount || 0, quotation.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    });

    const itemsTable = new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
        insideVertical: { style: BorderStyle.NONE },
      },
    });

    children.push(itemsTable);
    children.push(createSpacer(300, 300));

    // 4. Totals Card
    const totalsRows: TableRow[] = [];

    totalsRows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 60, type: WidthType.PERCENTAGE },
            children: [],
          }),
          new TableCell({
            width: { size: 22, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 100, bottom: 100, left: 160, right: 100 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Subtotal:',
                    color: cleanHex(tpl.labelColor),
                    font: FONT_FAMILY,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 18, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 100, bottom: 100, left: 100, right: 160 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: formatCurrency(quotation.subtotal || (quotation as any).subtotal || 0, quotation.currency),
                    color: cleanHex(tpl.bodyColor),
                    font: FONT_FAMILY,
                    size: 20,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    const quotationSubtotal = quotation.subtotal || (quotation as any).subtotal || 0;
    const headerTaxes = quotation.settings?.headerTax?.taxes || (taxData && taxData.type !== 'none' ? [taxData] : []);
    headerTaxes.forEach((t: any) => {
      if (!t.type || t.type === 'none') return;
      
      const taxLabel = `${t.label || 'Tax'} (${t.type === 'percentage' || t.type === 'vat' || t.type === 'sst'
        ? `${t.value}%`
        : formatCurrency(t.value, quotation.currency)}):`;
        
      let taxAmount = 0;
      if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
        taxAmount = (quotationSubtotal * (t.value || 0)) / 100;
      } else if (t.type === 'fixed') {
        taxAmount = t.value || 0;
      }

      totalsRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 60, type: WidthType.PERCENTAGE },
              children: [],
            }),
            new TableCell({
              width: { size: 22, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 160, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: taxLabel,
                      color: cleanHex(tpl.labelColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 100, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: formatCurrency(taxAmount, quotation.currency),
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    });

    if (discountData.type && discountData.type !== 'none') {
      const discountLabel = `Discount (${discountData.type === 'percentage'
        ? `${discountData.value}%`
        : formatCurrency(discountData.value, quotation.currency)}):`;
      const discountAmount = (quotation as any).discountAmount || quotation.totalDiscount || 0;

      totalsRows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 60, type: WidthType.PERCENTAGE },
              children: [],
            }),
            new TableCell({
              width: { size: 22, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 160, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: discountLabel,
                      color: cleanHex(tpl.labelColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              shading: { fill: cleanHex(tpl.totalBg) },
              margins: { top: 100, bottom: 100, left: 100, right: 160 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `-${formatCurrency(discountAmount, quotation.currency)}`,
                      color: cleanHex(tpl.bodyColor),
                      font: FONT_FAMILY,
                      size: 20,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    }

    const totalVal = (quotation as any).total || quotation.totalAmount || 0;
    totalsRows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 60, type: WidthType.PERCENTAGE },
            children: [],
          }),
          new TableCell({
            width: { size: 22, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 140, bottom: 140, left: 160, right: 100 },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 8, color: cleanHex(tpl.headingColor) },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Total:',
                    color: cleanHex(tpl.headingColor),
                    font: FONT_FAMILY,
                    size: 24,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 18, type: WidthType.PERCENTAGE },
            shading: { fill: cleanHex(tpl.totalBg) },
            margins: { top: 140, bottom: 140, left: 100, right: 160 },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 8, color: cleanHex(tpl.headingColor) },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: formatCurrency(totalVal, quotation.currency),
                    color: cleanHex(tpl.headingColor),
                    font: FONT_FAMILY,
                    size: 24,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );

    const totalsTable = new Table({
      rows: totalsRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
    });

    children.push(totalsTable);
    children.push(createSpacer(300, 300));

    // 5. Notes & Terms
    if (quotation.notes || quotation.terms) {
      const notesAndTermsChildren: Paragraph[] = [];

      if (quotation.notes) {
        notesAndTermsChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'Notes:',
                bold: true,
                size: 18,
                color: cleanHex(tpl.headingColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 60 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: quotation.notes,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 120 },
          })
        );
      }

      if (quotation.terms) {
        notesAndTermsChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'Terms & Conditions:',
                bold: true,
                size: 18,
                color: cleanHex(tpl.headingColor),
                font: FONT_FAMILY,
              }),
            ],
            spacing: { after: 60 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: quotation.terms,
                size: 18,
                color: cleanHex(tpl.labelColor),
                font: FONT_FAMILY,
              }),
            ],
          })
        );
      }

      const notesAndTermsTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: cleanHex(tpl.rowBorderColor) },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
          insideHorizontal: { style: BorderStyle.NONE },
          insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                margins: { top: 200, bottom: 0, left: 0, right: 0 },
                children: notesAndTermsChildren,
              }),
            ],
          }),
        ],
      });

      children.push(notesAndTermsTable);
    }

    const doc = new Document({
      background: {
        color: cleanHex(bgColor),
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                bottom: 1440,
                left: 1440,
                right: 1440,
              },
            },
          },
          children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting Word:', error);
    throw new Error('Failed to export Word document');
  }
}
